import React, { ReactElement, useMemo } from 'react';

import {
  Tooltip as MuiTooltip,
  styled,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import clsx from 'clsx';

import { Hint } from './Hint';
import styles from './Tooltip.module.scss';

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

type Props = {
  children: ReactElement;
  text: string;
  hotkey?: string;
  side?: TooltipSide;
  sideOffset?: number;
  align?: 'center' | 'start' | 'end';
  delayDuration?: number;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
};

const getTooltipPlacement = (
  side: TooltipSide,
  align: NonNullable<Props['align']>,
): TooltipProps['placement'] => {
  if (align === 'center') {
    return side;
  }

  return `${side}-${align}` as TooltipProps['placement'];
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    border: 0,
    boxShadow: 'none',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    margin: 0,
    maxWidth: 'none',
    padding: 0,
  },
}));

export const Tooltip = ({
  children,
  text,
  hotkey,
  side = 'top',
  sideOffset = 4,
  align = 'center',
  delayDuration = 200,
  className,
  contentClassName,
  disabled = false,
}: Props): JSX.Element => {
  const title = useMemo(
    () => (
      <span
        data-design-system-tooltip
        className={clsx(styles.content, contentClassName)}
      >
        <Hint text={text} hotkey={hotkey} />
      </span>
    ),
    [contentClassName, hotkey, text],
  );

  if (disabled) {
    return children;
  }

  return (
    <StyledTooltip
      title={title}
      placement={getTooltipPlacement(side, align)}
      enterDelay={delayDuration}
      className={className}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, sideOffset],
            },
          },
        ],
      }}
    >
      {children}
    </StyledTooltip>
  );
};

export type { Props as TooltipProps };
